import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";

/**
 * "Be Different" positioning section — sits between About and the
 * Music Store block (`HomePromoOffers`) on the landing page.  Speaks
 * to individuality and owning your sound.  Locale copy from
 * `beDifferent.*`; routing reuses `localisedTo("/music", lang)` so
 * EN → /music and AR → /ar/music, matching the existing music-store
 * CTA pattern.  RTL is inherited from the app-level `<html dir>`
 * toggle in LocaleContext; no per-section RTL overrides.
 */
export default function BeDifferent() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const navigate = useNavigate();

  return (
    <section
      id="be-different"
      data-testid="section-be-different"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-black"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p
            className="font-mono text-xs text-red-brand tracking-[0.3em] uppercase"
            data-testid="be-different-kicker"
          >
            {t("beDifferent.kicker")}
          </p>
          <h2
            className="font-anton mt-5 text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-white uppercase"
            data-testid="be-different-headline"
          >
            {t("beDifferent.headline")}
          </h2>
          <p
            className="mt-8 text-white/70 leading-relaxed text-base md:text-lg max-w-2xl"
            data-testid="be-different-body"
          >
            {t("beDifferent.body")}
          </p>
          <p
            className="mt-8 font-anton text-2xl md:text-3xl text-white uppercase tracking-tight"
            data-testid="be-different-closing"
          >
            {t("beDifferent.closing")}
          </p>
          <div className="mt-8">
            <button
              type="button"
              onClick={() => navigate(localisedTo("/music", lang))}
              className="btn-primary inline-flex items-center"
              data-testid="be-different-cta"
            >
              {t("beDifferent.cta")}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
